'use client'

import { forwardRef, useImperativeHandle } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { z } from 'zod'
import { PlusCircleIcon, SaveIcon, XIcon, ServerIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import TimeInput from '@/components/ui/time-input'
import { UnsavedChangesGuardDialog } from '@/components/partials'
import { useUnsavedChangesGuard } from '@/hooks'
import { CreateBackupScheduleParams, BackupScheduleType } from '@/types/backup'
import { DayOfWeekEnum, FormModeEnum } from '@/enums'

const schema = z
  .object({
    days: z.array(z.string()).min(1, 'at_least_one_day_required'),
    time: z.string().regex(/^\d{2}:\d{2}$/, 'invalid_time'),
    keepCount: z.number().min(1),
    includeDatabase: z.boolean(),
    includeStorage: z.boolean(),
    enabled: z.boolean()
  })
  .refine(data => data.includeDatabase || data.includeStorage, {
    message: 'at_least_one_target_required',
    path: ['includeDatabase']
  })

type FormValues = z.infer<typeof schema>

export type BackupScheduleFormRef = {
  handleValidationErrors: (error: any) => void
  reset: () => void
}

type PropsType = {
  mode: FormModeEnum
  initialData?: BackupScheduleType
  onSubmit: (data: CreateBackupScheduleParams) => void
  onSubmitAndNew?: (data: CreateBackupScheduleParams) => void
  onCancel: () => void
  isLoading?: boolean
}

const toTimeString = (hour: number, minute: number) =>
  `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`

const BackupScheduleForm = forwardRef<BackupScheduleFormRef, PropsType>(
  ({ mode, initialData, onSubmit, onSubmitAndNew, onCancel, isLoading }, ref) => {
    const t = useTranslations()

    const form = useForm<FormValues>({
      resolver: zodResolver(schema),
      defaultValues: initialData
        ? {
            days: initialData.days.map(d => d.toLowerCase()),
            time: toTimeString(initialData.hour, initialData.minute),
            keepCount: initialData.keepCount,
            includeDatabase: initialData.includeDatabase,
            includeStorage: initialData.includeStorage,
            enabled: initialData.enabled
          }
        : {
            days: [],
            time: '00:00',
            keepCount: 5,
            includeDatabase: true,
            includeStorage: true,
            enabled: true
          }
    })

    const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

    const {
      handleSubmit,
      control,
      watch,
      setValue,
      setError,
      formState: { errors }
    } = form

    const watchDays = watch('days')
    const watchEnabled = watch('enabled')

    useImperativeHandle(ref, () => ({
      handleValidationErrors: (error: any) => {
        const validationErrors = error.response?.data?.items
        if (validationErrors) {
          Object.entries(validationErrors).forEach(([field, message]) => {
            setError(field as keyof FormValues, { message: message as string })
          })
        }
      },
      reset: () => form.reset()
    }))

    const handleDayToggle = (day: string, checked: boolean) => {
      if (checked) {
        setValue('days', [...watchDays, day])
      } else {
        setValue(
          'days',
          watchDays.filter(d => d !== day)
        )
      }
    }

    const onFormSubmit = (data: FormValues) => {
      const { time, ...rest } = data
      const [hour, minute] = time.split(':').map(Number)
      onSubmit({ ...rest, hour, minute })
    }

    const onFormSubmitAndNew = (data: FormValues) => {
      const { time, ...rest } = data
      const [hour, minute] = time.split(':').map(Number)
      onSubmitAndNew?.({ ...rest, hour, minute })
    }

    const isCreate = mode === FormModeEnum.CREATE
    const isCreateModeWithSubmitAndNew = isCreate && !!onSubmitAndNew

    return (
      <form
        onSubmit={handleSubmit(isCreateModeWithSubmitAndNew ? onFormSubmitAndNew : onFormSubmit)}
        className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold">
                {isCreate ? t('create_backup_schedule') : t('edit_backup_schedule')}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
              <XIcon className="h-4 w-4" />
              <span className="hidden sm:inline">{t('cancel')}</span>
            </Button>
            {isCreateModeWithSubmitAndNew && (
              <Button type="button" onClick={() => handleSubmit(onFormSubmit)()} disabled={isLoading}>
                <SaveIcon className="h-4 w-4" />
                <span className="hidden sm:inline">{isLoading ? t('saving') : t('save')}</span>
              </Button>
            )}
            <Button type="submit" disabled={isLoading}>
              {isCreateModeWithSubmitAndNew ? <PlusCircleIcon className="h-4 w-4" /> : <SaveIcon className="h-4 w-4" />}
              <span className="hidden sm:inline">
                {isLoading ? t('saving') : isCreateModeWithSubmitAndNew ? t('save_and_add_new') : t('save')}
              </span>
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ServerIcon className="h-5 w-5" />
              {t('schedule_config')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <Label>{t('days')}</Label>
              <div className="flex flex-wrap gap-3">
                {Object.values(DayOfWeekEnum).map(day => (
                  <div key={day} className="flex items-center gap-2">
                    <Checkbox
                      id={`day-${day}`}
                      checked={watchDays.includes(day)}
                      onCheckedChange={checked => handleDayToggle(day, !!checked)}
                    />
                    <Label htmlFor={`day-${day}`} className="cursor-pointer">
                      {t(`day_${day}` as any)}
                    </Label>
                  </div>
                ))}
              </div>
              {errors.days && <p className="text-destructive text-sm">{t('at_least_one_day_required' as any)}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="time">{t('time')}</Label>
              <Controller
                name="time"
                control={control}
                render={({ field }) => (
                  <TimeInput
                    id="time"
                    showSeconds={false}
                    value={field.value}
                    onChange={field.onChange}
                    className="max-w-40"
                  />
                )}
              />
              {errors.time && <p className="text-destructive text-sm">{errors.time.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="keepCount">{t('keep_count')}</Label>
              <Controller
                name="keepCount"
                control={control}
                render={({ field }) => (
                  <Input
                    id="keepCount"
                    type="number"
                    min={1}
                    value={field.value}
                    onChange={e => field.onChange(Number(e.target.value))}
                    className="max-w-32"
                  />
                )}
              />
              {errors.keepCount && <p className="text-destructive text-sm">{errors.keepCount.message}</p>}
            </div>

            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-3">
                  <Controller
                    name="includeDatabase"
                    control={control}
                    render={({ field }) => (
                      <Checkbox id="includeDatabase" checked={field.value} onCheckedChange={field.onChange} />
                    )}
                  />
                  <Label htmlFor="includeDatabase" className="cursor-pointer">
                    {t('include_database')}
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <Controller
                    name="includeStorage"
                    control={control}
                    render={({ field }) => (
                      <Checkbox id="includeStorage" checked={field.value} onCheckedChange={field.onChange} />
                    )}
                  />
                  <Label htmlFor="includeStorage" className="cursor-pointer">
                    {t('include_storage')}
                  </Label>
                </div>
              </div>
              {errors.includeDatabase && (
                <p className="text-destructive text-sm">{t('at_least_one_target_required' as any)}</p>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Controller
                name="enabled"
                control={control}
                render={({ field }) => <Switch checked={field.value} onCheckedChange={field.onChange} />}
              />
              <Label className="cursor-pointer">{watchEnabled ? t('enabled') : t('disabled')}</Label>
            </div>
          </CardContent>
        </Card>
        <UnsavedChangesGuardDialog
          open={isNavigationBlocked}
          onConfirm={confirmNavigation}
          onCancel={cancelNavigation}
        />
      </form>
    )
  }
)

BackupScheduleForm.displayName = 'BackupScheduleForm'

export default BackupScheduleForm
